'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';

const InitialCompactViewportContext = createContext<boolean>(false);

type InitialCompactViewportProviderProps = {
    initialIsCompact: boolean;
    children: ReactNode;
};

/**
 * Plumb the server-derived compact viewport hint (from the request `User-Agent`) into a context
 * so client hooks can use it as their `useSyncExternalStore` server snapshot. This eliminates the
 * “desktop chrome flashes for ~10s on mobile” regression by making the very first SSR/hydration
 * render already match the device class, before `matchMedia` is queried.
 *
 * After hydration, the live `matchMedia('(max-width: 767px)')` snapshot is the source of truth and
 * keeps the layout in sync with viewport rotations or window resizes.
 */
export const InitialCompactViewportProvider = ({ initialIsCompact, children }: InitialCompactViewportProviderProps) => {
    const value = useMemo<boolean>(() => initialIsCompact, [initialIsCompact]);

    return <InitialCompactViewportContext.Provider value={value}>{children}</InitialCompactViewportContext.Provider>;
};

export const useInitialCompactViewport = (): boolean => useContext(InitialCompactViewportContext);
